import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Avatar,
  LinearProgress,
  IconButton
} from '@mui/material';
import {
  Collections as CollectionsIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as PreorderIcon,
  CalendarToday as CalendarIcon,
  Inventory as InventoryIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { toyService } from '../../services/toyService';
import { currencyService } from '../../services/currencyService';
import { useTranslation } from '../../hooks/useTranslation';
import { format } from 'date-fns';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`insights-tabpanel-${index}`}
      aria-labelledby={`insights-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function CollectionInsights() {
  const [toys, setToys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userCurrency, setUserCurrency] = useState('USD');
  const [tabValue, setTabValue] = useState(0);
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadData() {
    if (!currentUser?.uid) return;
    
    try {
      setLoading(true);
      const [toysData, currency] = await Promise.all([
        toyService.getToys(currentUser.uid),
        currencyService.getUserCurrency(currentUser.uid)
      ]);
      
      setToys(toysData);
      setUserCurrency(currency);
    } catch (error) {
      setError('Failed to load collection data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  function handleTabChange(event, newValue) {
    setTabValue(newValue);
  }

  // Calculate insights
  const insights = {
    totalToys: toys.length,
    totalValue: toys.reduce((sum, toy) => {
      const price = toy.preorder_price && toy.preorder_price > 0 && !toy.createdDate 
        ? toy.preorder_price 
        : (toy.price || 0);
      return sum + price;
    }, 0),
    totalQuantity: toys.reduce((sum, toy) => sum + (toy.qty || 1), 0),
    averagePrice: toys.length > 0 ? toys.reduce((sum, toy) => {
      const price = toy.preorder_price && toy.preorder_price > 0 && !toy.createdDate 
        ? toy.preorder_price 
        : (toy.price || 0);
      return sum + price;
    }, 0) / toys.length : 0
  };

  // Calculate category breakdowns
  const brandBreakdown = toys.reduce((acc, toy) => {
    if (toy.brand) {
      acc[toy.brand] = (acc[toy.brand] || 0) + 1;
    }
    return acc;
  }, {});

  const seriesBreakdown = toys.reduce((acc, toy) => {
    if (toy.series) {
      acc[toy.series] = (acc[toy.series] || 0) + 1;
    }
    return acc;
  }, {});

  const typeBreakdown = toys.reduce((acc, toy) => {
    if (toy.type) {
      acc[toy.type] = (acc[toy.type] || 0) + 1;
    }
    return acc;
  }, {});

  const scaleBreakdown = toys.reduce((acc, toy) => {
    if (toy.scale) {
      acc[toy.scale] = (acc[toy.scale] || 0) + 1;
    }
    return acc;
  }, {});

  // Separate owned and preorder items
  const ownedToys = toys.filter(toy => toy.createdDate);
  const preorderToys = toys.filter(toy => !toy.createdDate);

  const ownedValue = ownedToys.reduce((sum, toy) => sum + (toy.price || 0), 0);
  const preorderValue = preorderToys.reduce((sum, toy) => sum + (toy.preorder_price || 0), 0);

  // Get most valuable items
  const mostValuableItems = toys
    .map(toy => ({
      ...toy,
      displayPrice: toy.preorder_price && toy.preorder_price > 0 && !toy.createdDate 
        ? toy.preorder_price 
        : (toy.price || 0)
    }))
    .sort((a, b) => b.displayPrice - a.displayPrice)
    .slice(0, 5);

  // Get recent additions
  const recentAdditions = toys
    .filter(toy => toy.createdDate)
    .map(toy => ({
      ...toy,
      validDate: new Date(toy.createdDate)
    }))
    .sort((a, b) => b.validDate - a.validDate)
    .slice(0, 5);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)',
        color: 'white',
        py: { xs: 4, md: 6 },
        position: 'relative'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              onClick={() => navigate('/toys')}
              sx={{ color: 'white', mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              {t('insights.title')}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            {t('insights.subtitle')}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Overview Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <InventoryIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {insights.totalToys}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('insights.stats.totalItems')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <MoneyIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {userCurrency} {insights.totalValue.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('insights.stats.totalValue')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <CollectionsIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {insights.totalQuantity}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('insights.stats.totalQuantity')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {userCurrency} {insights.averagePrice.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('insights.stats.averagePrice')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={t('insights.overview.title')} />
            <Tab label={t('insights.categories.title')} />
            <Tab label={t('insights.preorders.title')} />
            <Tab label={t('insights.recent.title')} />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('insights.overview.collectionStatus')}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        {t('insights.overview.ownedItems')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {ownedToys.length} ({userCurrency} {ownedValue.toLocaleString()})
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(ownedToys.length / toys.length) * 100} 
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        {t('insights.overview.preorderItems')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {preorderToys.length} ({userCurrency} {preorderValue.toLocaleString()})
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(preorderToys.length / toys.length) * 100} 
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('insights.overview.mostValuableItems')}
                  </Typography>
                  <List>
                    {mostValuableItems.map((toy, index) => (
                      <ListItem key={toy.id} sx={{ px: 0 }}>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            {index + 1}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={toy.name}
                          secondary={`${toy.brand} - ${userCurrency} ${Math.round(toy.displayPrice).toLocaleString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Categories Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('insights.categories.topBrands')}
                  </Typography>
                  {Object.entries(brandBreakdown)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 5)
                    .map(([brand, count]) => (
                      <Box key={brand} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">{brand}</Typography>
                          <Typography variant="body2">{count}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(count / Math.max(...Object.values(brandBreakdown))) * 100} 
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('insights.categories.topSeries')}
                  </Typography>
                  {Object.entries(seriesBreakdown)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 5)
                    .map(([series, count]) => (
                      <Box key={series} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">{series}</Typography>
                          <Typography variant="body2">{count}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(count / Math.max(...Object.values(seriesBreakdown))) * 100} 
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('insights.categories.topTypes')}
                  </Typography>
                  {Object.entries(typeBreakdown)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 5)
                    .map(([type, count]) => (
                      <Box key={type} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">{type}</Typography>
                          <Typography variant="body2">{count}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(count / Math.max(...Object.values(typeBreakdown))) * 100} 
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('insights.categories.topScales')}
                  </Typography>
                  {Object.entries(scaleBreakdown)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 5)
                    .map(([scale, count]) => (
                      <Box key={scale} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">{scale}</Typography>
                          <Typography variant="body2">{count}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(count / Math.max(...Object.values(scaleBreakdown))) * 100} 
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Preorders Tab */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('insights.preorders.preorderSummary')}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <PreorderIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {preorderToys.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('insights.preorders.items')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <MoneyIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {userCurrency} {preorderValue.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('insights.preorders.totalValue')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              {preorderToys.length > 0 && (
                <List>
                  {preorderToys.map((toy) => (
                    <ListItem key={toy.id} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <PreorderIcon sx={{ color: 'primary.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={toy.name}
                        secondary={`${toy.brand} - ${userCurrency} ${Math.round(toy.preorder_price || 0).toLocaleString()}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Recent Tab */}
        <TabPanel value={tabValue} index={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('insights.recent.recentAdditions')}
              </Typography>
              {recentAdditions.length > 0 ? (
                <List>
                  {recentAdditions.map((toy) => (
                    <ListItem key={toy.id} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CalendarIcon sx={{ color: 'primary.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={toy.name}
                        secondary={`${toy.brand} - ${format(toy.validDate, 'MMM dd, yyyy')}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {t('insights.recent.recentAdditions')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </TabPanel>
      </Container>

      {/* Footer */}
      <Box sx={{
        bgcolor: 'grey.100',
        py: 4,
        mt: 8,
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                ToyPort
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The ultimate toy and model collection manager. Built by collectors, for collectors.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Features
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • Complete collection management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • Multi-currency support
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • Preorder tracking
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Cloud sync
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Support
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Free to use with no hidden costs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your collection is private and secure
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              © 2024 ToyPort. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default CollectionInsights; 